import { assertSafeInteger } from '@kenocyber/shared';

/**
 * Integer vector maths for a simulation, with no trigonometry anywhere.
 *
 * ## Where this lived, and why it moved
 *
 * It was `pvp/vectors.ts`, and nothing in it ever knew it was a duel: there is
 * no arena, no seat, no balance constant and no import of either mode. The open
 * world's authoritative room needs the SAME arithmetic - a movement that
 * normalised differently would be a second physics - and reaching into `pvp/`
 * for it would have made the duel a dependency of the world.
 *
 * So it is a leaf beside `damage/` and `hull/`: both modes import it, and it
 * imports neither. `pvp/vectors.ts` re-exports this file, so every existing
 * import line and every existing duel behaves exactly as it did.
 *
 * ## Why there is no angle in a simulation
 *
 * `Math.sin`, `Math.cos` and `Math.atan2` are implementation-defined in the last
 * bit, and Node and Chromium already disagree about `atan2` elsewhere in this
 * repository (`CLAUDE.md`). A duel is decided by whether a shot touched a hull,
 * so a last-bit difference is a difference between winning and losing.
 *
 * The fix is not to compare with a tolerance - it is to never produce the
 * number. A direction here is a **normalised integer vector** scaled by
 * {@link VECTOR_SCALE}, and every operation on it is integer multiply, integer
 * add and integer division. Rotation exists exactly once in this feature, in
 * the renderer, where it decides which way a sprite points and nothing else.
 *
 * ## Why `Math.sqrt` is allowed and `Math.sin` is not
 *
 * IEEE 754 REQUIRES square root to be correctly rounded, so every conforming
 * engine returns the identical double. It is used only as a seed, and
 * {@link isqrt} then corrects it into an exact integer floor, so even that
 * guarantee is not leaned on.
 */

/** Directions are unit vectors multiplied by this. A power of two on purpose. */
export const VECTOR_SCALE = 65_536;

/**
 * `round(VECTOR_SCALE / sqrt(2))`, the diagonal component of a normalised
 * eight-way movement vector.
 *
 * Written down rather than computed so the constant cannot drift with a
 * library, and so holding two keys can never be faster than holding one.
 */
export const VECTOR_DIAGONAL = 46_341;

/**
 * Integer division truncating toward zero.
 *
 * This is arithmetic, NOT input coercion. The rule against `Math.trunc` in
 * `CLAUDE.md` is about accepting a malformed value silently; here both operands
 * are already validated integers and the truncation is the defined semantics of
 * fixed-point division. Every call site is inside this module or the tick.
 */
export function divTrunc(numerator: number, denominator: number): number {
  if (denominator === 0) {
    throw new RangeError('division by zero in pvp vector maths');
  }
  const quotient = numerator / denominator;
  return quotient < 0 ? Math.ceil(quotient) : Math.floor(quotient);
}

/**
 * Exact integer square root.
 *
 * Seeded from `Math.sqrt` and then corrected in both directions, so the answer
 * is `floor(sqrt(value))` exactly whatever the seed rounded to.
 */
export function isqrt(value: number): number {
  assertSafeInteger(value, 'isqrt operand');
  if (value < 0) {
    throw new RangeError(`isqrt operand must not be negative, received ${value}`);
  }
  if (value < 2) {
    return value;
  }
  let root = Math.floor(Math.sqrt(value));
  while (root > 0 && root * root > value) {
    root -= 1;
  }
  while ((root + 1) * (root + 1) <= value) {
    root += 1;
  }
  return root;
}

export interface IntVector {
  readonly x: number;
  readonly y: number;
}

/**
 * Turns a raw aim vector into a direction of length {@link VECTOR_SCALE}.
 *
 * Returns `null` for the zero vector rather than inventing a direction: a
 * player whose pointer is exactly on their own hull is not aiming anywhere, and
 * the room keeps the last direction instead of snapping east. Guessing here
 * would make a ship spin when the mouse crossed its centre.
 *
 * The magnitude the client sent is DISCARDED. Only direction survives, which is
 * why a client cannot shoot further or faster by sending a longer vector.
 */
export function normalise(x: number, y: number): IntVector | null {
  assertSafeInteger(x, 'aim x');
  assertSafeInteger(y, 'aim y');
  const lengthSquared = x * x + y * y;
  if (lengthSquared === 0) {
    return null;
  }
  const length = isqrt(lengthSquared);
  if (length === 0) {
    return null;
  }
  return { x: divTrunc(x * VECTOR_SCALE, length), y: divTrunc(y * VECTOR_SCALE, length) };
}

/**
 * The normalised movement direction for a pair of axes in `{-1, 0, 1}`.
 *
 * Diagonals use {@link VECTOR_DIAGONAL}, so travelling north-east covers the
 * same ground per tick as travelling north. Without this, the fastest way
 * across the arena would be to hold two keys - a bug old enough to have a name,
 * and one that decides duels.
 */
export function movementDirection(mx: number, my: number): IntVector {
  const x = Math.sign(mx);
  const y = Math.sign(my);
  if (x === 0 && y === 0) {
    return { x: 0, y: 0 };
  }
  if (x !== 0 && y !== 0) {
    return { x: x * VECTOR_DIAGONAL, y: y * VECTOR_DIAGONAL };
  }
  return { x: x * VECTOR_SCALE, y: y * VECTOR_SCALE };
}

/** Scales a direction by a per-tick distance, in the same integer space. */
export function scaleDirection(direction: IntVector, magnitude: number): IntVector {
  return {
    x: divTrunc(direction.x * magnitude, VECTOR_SCALE),
    y: divTrunc(direction.y * magnitude, VECTOR_SCALE),
  };
}

/**
 * Squared distance between two points.
 *
 * Squared, so no root is taken and no precision is lost.
 *
 * ## The caller owns the bound, and has to state it
 *
 * Exactness here is a property of the SPACE the caller works in, never of this
 * function. The duel's arena is 24 000 000 by 15 000 000 milli-units, so the
 * widest separation squared is about 8.1e14 against a safe ceiling of 9.0e15 -
 * comfortable, which is why the duel calls this freely.
 *
 * A wider space is not. An open-world quadrant is 81 600 000 milli-units a
 * side, and `dx * dx + dy * dy` across one already leaves the exact-integer
 * range - so a caller there has to establish that both axes are short BEFORE
 * anything is squared. `isAtLeastApartMu` in `open-world/geometry.ts` is that
 * discipline written down, and this note is here so the next caller goes
 * looking for it instead of assuming the duel's numbers.
 */
export function distanceSquared(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** Clamps a value into `[min, max]`. Both bounds are inclusive. */
export function clamp(value: number, min: number, max: number): number {
  if (min > max) {
    throw new RangeError(`clamp bounds are inverted: [${min}, ${max}]`);
  }
  return value < min ? min : value > max ? max : value;
}
